import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { ApiError } from '../api/client'
import {
  serviceRequestQuery,
  serviceRequestsQuery,
  type ShopChildTicket,
  type ShopItem,
  type ShopRequestPage,
  type ShopRequestRow,
} from '../api/easeedeskServiceShop'
import { moduleOf, shellQuery } from '../api/shell'
import { Forbidden } from '../ui/Forbidden'
import { Modal } from '../ui/Modal'
import { Spinner } from '../ui/Spinner'
import { usePageStyles } from '../ui/usePageStyles'
import { CartModal, PageHead, ShopTabs } from './ServiceShopPage'

/**
 * "My requests" - `Service_shop::requests()` si `Service_shop::request()`,
 * machetele `requests.php` si `request.php`.
 *
 * Tabelul de sus spune ce s-a cerut si cat mai e de asteptat; fereastra de
 * detaliu spune, pentru fiecare articol, unde a ajuns TICHETUL lui, ce tichete
 * copil s-au nascut din el, si - jos - ce s-a intamplat, in ordine.
 *
 * NUMAI CITIRE. Ecranul asta n-avea butoane care scriu nici in portal, deci nu
 * lipseste nimic din el; ce lipseste sunt butoanele COSULUI, si alea sunt in
 * fereastra cosului - vezi `ServiceShopPage`.
 *
 * Textul scris de om - subiectul unui tichet copil, justificarea, nota de
 * decizie, numele serviciului - trece prin JSX, care il scapa singur. Nicaieri
 * pe ecranul asta nu se pune HTML brut.
 */
export function ServiceRequestsPage() {
  const [open, setOpen] = useState(0)
  const [cartOpen, setCartOpen] = useState(false)

  const page = useQuery(serviceRequestsQuery())
  const shell = useQuery(shellQuery(moduleOf(typeof window === 'undefined' ? '' : window.location.pathname)))
  usePageStyles(['css/adm.css', 'css/service_shop.css'], shell.data?.version ?? '')

  if (page.error instanceof ApiError && page.error.status === 403) return <Forbidden />
  if (!page.data) return <Spinner />

  const requests = page.data.requests
  /* `status === 2` e REQ_SUBMITTED: cate mai sunt deschise. */
  const stillOpen = requests.filter((one) => one.status === 2).length

  return (
    <div id="content">
      <PageHead
        title="My requests"
        icon="fa-clipboard-list"
        tone="blue-mauve"
        note="What you asked for, and where each one got to."
        stats={[
          { value: requests.length, label: 'in total', tone: 'grey' },
          { value: stillOpen, label: 'still open', tone: 'blue-hoki' },
        ]}
      />

      <ShopTabs active="requests" cartCount={page.data.cartCount} canCatalog={page.data.canCatalog} onCart={() => setCartOpen(true)} />

      <div className="adm-card adm-tone-blue-mauve">
        <div className="adm-card-head">
          <h5>
            <i className="fas fa-history"></i> Requests
          </h5>
          <span className="adm-card-note">newest first</span>
        </div>

        {requests.length === 0 ? (
          <div className="adm-empty-inline">
            You have not asked for anything yet. <a href="/app/service_shop">Browse the shop</a>.
          </div>
        ) : (
          <table className="table adm-list" style={{ marginBottom: 0 }}>
            <thead>
              <tr>
                <th style={{ width: '80px' }} className="adm-numeric">
                  Request
                </th>
                <th>Sent</th>
                <th className="adm-numeric">Items</th>
                <th>State</th>
                <th style={{ width: '130px' }}>Last update</th>
                <th style={{ width: '90px' }}></th>
              </tr>
            </thead>
            <tbody>
              {requests.map((one) => (
                <RequestRow one={one} onOpen={() => setOpen(one.ID)} key={one.ID} />
              ))}
            </tbody>
          </table>
        )}
      </div>

      <RequestModal request={open} onClose={() => setOpen(0)} />
      <CartModal open={cartOpen} onClose={() => setCartOpen(false)} />
    </div>
  )
}

function RequestRow({ one, onOpen }: { one: ShopRequestRow; onOpen(): void }) {
  const quiet = one.waiting === 0 && one.pending === 0 && one.running === 0 && one.rejected === 0

  return (
    <tr>
      <td className="adm-numeric">#{one.ID}</td>
      <td className="adm-muted">{one.submitted_at.slice(0, 16)}</td>
      <td className="adm-numeric">{one.items}</td>
      <td>
        {/*
          Se arata ce asteapta, nu ce s-a incheiat: cine intra aici vrea sa stie
          daca mai are de asteptat, iar o cerere cu trei articole are trei
          raspunsuri diferite. Starea cererii singura ar ascunde asta.

          `waiting` se mai vede numai pe cererile plecate inainte ca aprobarea sa
          treaca pe tichet. Nu se mai scrie nimic acolo, dar randurile vechi
          exista.
        */}
        {one.waiting > 0 && (
          <span className="shop-state shop-state-waiting">
            <i className="fas fa-hourglass-half"></i> {one.waiting} waiting
          </span>
        )}
        {/*
          Trimis, dar fara tichet. Ar trebui sa nu se vada niciodata: tichetele
          se nasc in aceeasi apasare. Cand se vede, serviciul e configurat pe
          jumatate - tip fara flux, furnizor fara prioritate - iar fara randul
          asta cererea ar fi aratat "done".
        */}
        {one.pending > 0 && (
          <span className="shop-state shop-state-waiting">
            <i className="fas fa-exclamation-triangle"></i> {one.pending} without a ticket
          </span>
        )}
        {one.running > 0 && (
          <span className="shop-state shop-state-running">
            <i className="fas fa-cog"></i> {one.running} in progress
          </span>
        )}
        {one.rejected > 0 && (
          <span className="shop-state shop-state-rejected">
            <i className="fas fa-times"></i> {one.rejected} rejected
          </span>
        )}
        {quiet && (
          <span className="shop-state shop-state-done">
            <i className="fas fa-check"></i> done
          </span>
        )}
      </td>
      <td className="adm-muted adm-nowrap">
        {/* Cand s-a miscat ultimul tichet al cererii. Gol cat timp niciunul n-a
            fost deschis inca - acolo n-are ce spune. */}
        {one.ticket_updated === '' ? <span className="adm-muted">&mdash;</span> : one.ticket_updated.slice(0, 16)}
      </td>
      <td className="text-right">
        <button type="button" className="btn btn-link btn-xs shopOpen" data-id={one.ID} onClick={onOpen}>
          details
        </button>
      </td>
    </tr>
  )
}

/**
 * Iconita starii unui tichet dupa TIPUL ei (1 activa, 2 rezolvata, 3 finala).
 * N-are rost sa ghicim din nume ce inseamna o stare cand tipul o spune.
 */
const TYPE_ICON: Record<number, string> = { 1: 'fa-cog', 2: 'fa-check', 3: 'fa-flag-checkered' }

/** Clasa de culoare a starii MAGAZINULUI - se vede doar cat timp nu exista tichet. */
const STATE_CLASS: Record<number, string> = {
  1: 'cart', 2: 'waiting', 3: 'waiting', 4: 'rejected', 5: 'running', 6: 'done',
}

const STATE_ICON: Record<number, string> = {
  1: 'fa-shopping-cart', 2: 'fa-hourglass-half', 3: 'fa-paper-plane', 4: 'fa-times', 5: 'fa-cog', 6: 'fa-ban',
}

/** Tonul unui rand din cronologie: verde la aprobat / tichet creat, rosu la respins / anulat. */
const EVENT_TONE: Record<number, string> = { 2: 'is-good', 4: 'is-good', 3: 'is-bad', 5: 'is-bad' }

/**
 * "In lucru" inseamna stare de tip 1, adica activa. Tipurile 2 (rezolvata) si 3
 * (finala) inseamna ca acolo nu mai are cine sa munceasca - aceeasi impartire pe
 * care o foloseste toata platforma, nu una a magazinului.
 *
 * Fara stare citita, tichetul se socoteste in lucru: necunoscutul nu are voie sa
 * arate ca "gata".
 */
function stillWorking(kids: ShopChildTicket[]): boolean {
  return kids.some((kid) => kid.status_type === 0 || kid.status_type === 1)
}

function RequestModal({ request, onClose }: { request: number; onClose(): void }) {
  const q = useQuery(serviceRequestQuery(request))
  const data = q.data

  return (
    <Modal
      id="shopRequestModal"
      open={request > 0}
      onClose={onClose}
      labelledBy="shopRequestModalLabel"
      /*
       * `modal-lg`, ca in macheta: `requests.php` deschide fereastra cu
       * `<div class="modal-dialog modal-lg">`. Fara ea iese la 600px in loc de
       * 900, iar tabelul de articole - patru coloane, dintre care una poarta
       * starea tichetului, cine il tine si de cand - se indeasa pe trei randuri
       * la fiecare articol.
       */
      large
      /*
       * Butonul de jos. `request.php` are `modal-footer` cu unul singur,
       * „Close"; aici lipsea, si singurul drum afara ramasese crucea din colt.
       */
      footer={
        <button type="button" className="btn btn-default" onClick={onClose}>
          Close
        </button>
      }
      title={
        data ? (
          <>
            Request #{data.request.ID}{' '}
            <small className="text-muted">
              &mdash; {data.request.requester_name}, {data.request.submitted_at}
            </small>
          </>
        ) : (
          'Request'
        )
      }
    >
      {/*
        Cand citirea pica, se SPUNE. Pana acum `!data` insemna „inca se
        incarca", deci o cerere cazuta - 404 pe o cerere care nu e a ta, sau o
        eroare de server - lasa rotita invartindu-se la nesfarsit. Portalul
        scria mesajul in `#shopWarning`; aici se scrie in fereastra, unde se
        uita omul.
      */}
      {q.error ? (
        <div className="adm-empty-inline">
          {q.error instanceof ApiError && q.error.status === 404
            ? 'That request is not yours, or it no longer exists.'
            : 'The request could not be loaded. Try again.'}
        </div>
      ) : !data ? (
        <Spinner />
      ) : (
        <RequestBody data={data} />
      )}
    </Modal>
  )
}

function RequestBody({ data }: { data: ShopRequestPage }) {
  return (
    <>
      {data.request.note !== '' && (
        <p className="adm-muted" style={{ fontStyle: 'italic', marginBottom: '18px' }}>
          &ldquo;{data.request.note}&rdquo;
        </p>
      )}

      <table className="table adm-list">
        <thead>
          <tr>
            <th>Service</th>
            <th style={{ width: '60px' }} className="adm-numeric">
              Qty
            </th>
            <th style={{ width: '170px' }}>Ticket state</th>
            <th style={{ width: '110px' }}>Ticket</th>
          </tr>
        </thead>
        <tbody>
          {data.items.map((item) => (
            <ItemRows item={item} kids={data.children[String(item.ticket_id)] ?? []} key={item.ID} />
          ))}
        </tbody>
      </table>

      <h5
        style={{
          margin: '24px 0 14px',
          fontSize: '13px',
          fontWeight: 650,
          letterSpacing: '.03em',
          textTransform: 'uppercase',
          color: '#8c97a1',
        }}
      >
        What happened
      </h5>

      {data.events.length === 0 ? (
        <div className="adm-empty-inline">Nothing recorded yet.</div>
      ) : (
        <ul className="shop-timeline">
          {data.events.map((event) => (
            <li className={EVENT_TONE[event.event] ?? ''} key={event.ID}>
              <strong>{event.word || '—'}</strong>{' '}
              {event.who !== '' ? (
                <>by {event.who}</>
              ) : (
                /* Lipsa numelui e informatia: a facut-o sistemul, nu un om care
                   a uitat sa fie inregistrat. */
                <span className="adm-muted">by the system</span>
              )}
              {event.detail !== '' && (
                <>
                  <br />
                  <span className="adm-muted">{event.detail}</span>
                </>
              )}
              <time>{event.created_at}</time>
            </li>
          ))}
        </ul>
      )}
    </>
  )
}

/**
 * Un articol si tichetele lui copil.
 *
 * Starea unui articol e starea TICHETULUI lui. Coloana arata pana acum starea
 * inventata de magazin - "Waiting for approval", "Approved" - dintr-o vreme in
 * care magazinul isi tinea singur aprobarile. Aia ar minti in amandoua
 * sensurile: articolul ar scrie "Sent" cat timp tichetul lui sta in `Pending
 * Approval`, si "In progress" mult dupa ce tichetul a fost inchis. Starea
 * magazinului ramane numai cat timp nu exista tichet.
 */
function ItemRows({ item, kids }: { item: ShopItem; kids: ShopChildTicket[] }) {
  const ticketHref = `/app/easeedesk/ticket/${item.ticket_id}`
  const hasWho = item.ticket_assignee_name !== '' || item.ticket_group_name !== '' || item.ticket_updated !== ''

  return (
    <>
      <tr>
        <td>
          {item.name_at_request}
          {item.approver_group_name !== '' && (
            <>
              <br />
              {/* "Ar trebui sa aprobe", nu "a aprobat": rolul de pe catalog e
                  informativ de cand decizia se ia pe tichet. */}
              <span className="adm-muted" style={{ fontSize: '11px' }}>
                to be approved by {item.approver_group_name}
              </span>
            </>
          )}
          {item.decision_note !== '' && (
            <>
              <br />
              <span className="adm-muted" style={{ fontSize: '11px', fontStyle: 'italic' }}>
                &ldquo;{item.decision_note}&rdquo;
              </span>
            </>
          )}
        </td>
        <td className="adm-numeric">{item.quantity}</td>
        <td>
          {item.ticket_status_name !== '' ? (
            <a
              className="shop-state shop-state-ticket"
              style={item.ticket_status_color === '' ? undefined : { color: item.ticket_status_color }}
              href={ticketHref}
              target="_blank"
              rel="noopener noreferrer"
            >
              <i className={`fas ${TYPE_ICON[item.ticket_status_type] ?? 'fa-circle'}`}></i> {item.ticket_status_name}
            </a>
          ) : (
            /* Fara tichet inca. Singurul moment in care starea magazinului mai
               are ce spune. */
            <span className={`shop-state shop-state-${STATE_CLASS[item.status] ?? 'done'}`}>
              <i className={`fas ${STATE_ICON[item.status] ?? 'fa-circle'}`}></i> {item.state || '—'}
            </span>
          )}

          {/*
            Cine tine tichetul acum, si de cand. Starea singura - "In progress" -
            nu raspunde la intrebarea cu care omul intra aici: cine se ocupa, si
            mai am de asteptat? Sub stare, marunt: raspunsul principal ramane
            starea.
          */}
          {item.ticket_id > 0 && hasWho && (
            <div className="adm-muted" style={{ fontSize: '11px', marginTop: '3px' }}>
              {item.ticket_assignee_name !== '' ? (
                <>
                  <i className="fas fa-user"></i> {item.ticket_assignee_name}
                </>
              ) : item.ticket_group_name !== '' ? (
                <>
                  <i className="fas fa-users"></i> {item.ticket_group_name}
                </>
              ) : (
                <>
                  <i className="fas fa-user-slash"></i> nobody yet
                </>
              )}
              {item.ticket_assignee_name !== '' && item.ticket_group_name !== '' && <> &middot; {item.ticket_group_name}</>}
              {item.ticket_updated !== '' && <> &middot; {item.ticket_updated.slice(0, 16)}</>}
            </div>
          )}
        </td>
        <td>
          {item.ticket_id > 0 ? (
            <a href={ticketHref} target="_blank" rel="noopener noreferrer">
              #{item.ticket_id}
            </a>
          ) : (
            <span className="adm-muted">&mdash;</span>
          )}
        </td>
      </tr>

      {kids.map((kid) => (
        <tr key={kid.ID}>
          <td style={{ paddingLeft: '28px' }}>
            {/* Sageata spune ca randul atarna de cel de deasupra. Fara ea, un
                copil arata ca un al doilea serviciu cerut, iar omul se intreaba
                de ce a cerut ceva ce n-a cerut. */}
            <span className="adm-muted">&#8627;</span> {kid.subject}
            <br />
            <span className="adm-muted" style={{ fontSize: '11px' }}>
              needed by {item.name_at_request}
            </span>
          </td>
          <td className="adm-numeric">
            <span className="adm-muted">&mdash;</span>
          </td>
          <td>
            {kid.status_name !== '' ? (
              <a
                className="shop-state shop-state-ticket"
                style={kid.status_color === '' ? undefined : { color: kid.status_color }}
                href={`/app/easeedesk/ticket/${kid.ID}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <i className={`fas ${TYPE_ICON[kid.status_type] ?? 'fa-circle'}`}></i> {kid.status_name}
              </a>
            ) : (
              <span className="adm-muted">&mdash;</span>
            )}
          </td>
          <td>
            <a href={`/app/easeedesk/ticket/${kid.ID}`} target="_blank" rel="noopener noreferrer">
              #{kid.ID}
            </a>
          </td>
        </tr>
      ))}

      {kids.length > 0 && !stillWorking(kids) && (
        <tr>
          <td colSpan={4} style={{ paddingLeft: '28px' }}>
            <span className="adm-muted" style={{ fontSize: '12px' }}>
              <i className="fas fa-check"></i>{' '}
              {/* Se spune ce e adevarat - nu mai asteapta nimic - si atat. Nicio
                  inchidere automata si niciun buton care sa para obligatoriu:
                  unele dintre tichetele astea pot fi respinse, iar ce se face cu
                  cererea dupa aia e o decizie de om. */}
              Nothing left to wait for: every service this one depends on has been dealt with. Closing the request is
              still somebody&rsquo;s decision.
            </span>
          </td>
        </tr>
      )}
    </>
  )
}
